import { View } from 'react-native'
import Typography from '@ui/typography'
import { Collapsible } from '@ui/animated/Collapsible'
import { useTranslation } from 'react-i18next'
import { SegmentedOption } from '@ui/elements/Segmented/types'
import { useEffect, useMemo, useState } from 'react'
import { ChannelType } from '@ca/api/modules/notfications/Notifications.types'
import { useCustomer } from 'hooks/useSession'
import Segmented from '@ui/elements/Segmented'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import { trpc } from 'api'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { getFirstExistingKey } from 'utils/i18nUtils'
import { ErrorCause } from '@ca/common/types'

const NotificationError = ({
  cause,
  seconds,
}: {
  cause: ErrorCause | undefined
  seconds: number
}) => {
  const { t, i18n } = useTranslation(['payment', 'toasts', 'ui'])
  const text = 'payment:status.sendPaymentLink'

  const key = getFirstExistingKey(i18n, [
    `${text}.status.error.${cause?.code}`,
    `${text}.status.error.default`,
    `toasts:error.default.desc`,
  ])

  if (!key) return null

  return (
    <Typography variant="SM" className="color-red-600 pb-8">
      {t(key, { seconds: cause?.seconds || seconds })}
    </Typography>
  )
}

const useChannelValue = (channelType: ChannelType | null) => {
  const customer = useCustomer()

  if (!customer) return undefined
  if (channelType === 'email') return customer.email
  if (channelType === 'mobile') return customer.mobile
  return undefined
}

export default function SendPaymentLink({ link }: { link: string }) {
  const { t, i18n } = useTranslation(['payment', 'ui'])
  const text = 'payment:status.sendPaymentLink'

  const customer = useCustomer()
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined
  )
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )

  const [channel, setChannel] = useState<ChannelType | null>(
    customer?.email ? 'email' : customer?.mobile ? 'mobile' : null
  )

  const notifyMutation = trpc.customer.notify.useMutation()

  const channelTypes: SegmentedOption<ChannelType>[] = useMemo(() => {
    return [
      {
        id: 'email',
        label: t('ui:email'),
        disabled: !customer?.email,
      },
      {
        id: 'mobile',
        label: t('ui:mobile'),
        disabled: !customer?.mobile,
      },
    ]
  }, [t, customer?.email, customer?.mobile])

  const channelValue = useChannelValue(channel)

  const error = notifyMutation.error
  const isSuccess = notifyMutation.isSuccess
  const isError = notifyMutation.isError
  const isLoading = notifyMutation.isPending

  const config = useRemoteConfig()

  const handleSend = () => {
    if (!channel || !channelValue) return
    notifyMutation.mutate({
      channel,
      notification: {
        type: 'paylink',
        url: link,
      },
      region: config?.region.countryCode || 'de',
    })
  }

  const onChannelChange = (value: SegmentedOption<ChannelType>) => {
    setChannel(value.id)
    setErrorMessage(undefined)
    setSuccessMessage(undefined)
  }

  useEffect(() => {
    if (isError) {
      setErrorMessage(error?.message || error?.data?.cause?.message || 'error')
    }
  }, [isError, error])

  useEffect(() => {
    if (isSuccess) {
      setSuccessMessage(t('ui:sent'))
    }
  }, [isSuccess])

  return (
    <View className="flex w-full flex-col border-t border-black/10">
      <View className="flex flex-col gap-0 px-8">
        <Collapsible open={!successMessage && !errorMessage}>
          <Typography variant="SM" className="color-desc pt-8">
            {t(`${text}.status.initial`)}
          </Typography>
        </Collapsible>
        <View className="flex w-full  flex-row gap-8 py-8">
          <Segmented
            options={channelTypes}
            value={channelTypes.find((option) => option.id === channel)}
            disabled={isLoading}
            onChange={onChannelChange}
          />
          <Input
            readonly
            value={channelValue || ''}
            className="flex-1"
            errorMessage={errorMessage}
            successMessage={isSuccess ? t('ui:sent') : undefined}
          />
          <Button
            variant="outlined"
            label={t('ui:send')}
            onPress={handleSend}
            loading={isLoading}
          />
        </View>
        <Collapsible open={!!successMessage || !!errorMessage}>
          {isSuccess && (
            <Typography variant="SM" className="color-desc pb-8">
              {t(`${text}.status.success`, { seconds: 30 })}
            </Typography>
          )}
          {errorMessage && (
            <NotificationError cause={error?.data?.cause} seconds={30} />
          )}
        </Collapsible>
      </View>
    </View>
  )
}
